import React, { useRef } from 'react'
import { Row, Col } from 'react-flexbox-grid'
import { useTranslation } from 'react-i18next'
import { Text as InformedText } from 'informed'
import get from 'lodash/get'
import { RadioGroup } from 'informed'

import { ContentBlock, ExpandableBlocks } from 'components/ContentBlock'
import Button from 'components/Button'
import Divider from 'components/Divider'
import { Form, Text, Number as NumberInput, Checkbox, DateTimePicker, Select, Radio, PhoneNumber } from 'components/forms'
import listCreditClients from 'sections/creditClient/gql/listCreditClients'
import { listUsers } from 'sections/user/gql'
import loadAppConstants from 'gql/query/loadAppConstants'
import getDiscountGroupNames from 'config/localServer/query/getDiscountGroupNames'
import { notEmpty } from 'helpers/validators'

import SendMessageButton from './SendMessageButton'
import Transactions from './Transactions'
import { ReactComponent as AppleIcon } from './media/apple_wallet.svg'
import { ReactComponent as GoogleIcon } from './media/google_wallet.svg'
import { StyledIconContainer } from './styled'

const immutableFields = ['accessCode', 'lastUpdatedAt', 'balance', 'points', 'rfid']

const Loyaltycard = ({ appData, data = {}, formData, onChangeMultiEdit, apiRef, ConfirmButton, ConfirmModal, mutations: { updateItem, createItem }, newRecord, multiEdit }) => {
	const expandableBlocksRef = useRef()
	const [t, i18n] = useTranslation()
	const langCode = i18n.language === 'English' ? 1 : 2

	const handleSubmit = ({ user, creditClient, cardType, ...vals }) => {
		let mutableFields = Object.keys(vals).reduce((outObj, fieldName) => {
			if(immutableFields.indexOf(fieldName) === -1) {
				outObj[fieldName] = vals[fieldName]
			}
			return outObj
		}, {})
		if(newRecord) {
			createItem(mutableFields)
		} else {
			updateItem(mutableFields)
		}
	}

	return(
		<ExpandableBlocks>
			<ContentBlock startExpanded label={t('loyalty_card_section.sub_sections.loyalty_card')} additionalSwitcher>
				<Form
					apiRef={apiRef}
					onChange={(e) => multiEdit?.isActive && onChangeMultiEdit(e, formData)}
					initialValues={{ ...formData, lastUpdatedAt: data.lastUpdatedAt }}
					onSubmit={handleSubmit}
					smartSubmit={(!newRecord && !multiEdit?.isActive)}
				>
					{({ formState: { values }, formApi, formState }) => (
						<>
							{newRecord &&
								<RadioGroup field='isVirtual'>
									<Radio label={t('loyalty_card_section.strings.plastic')}/>
									<Radio value={true} label={t('loyalty_card_section.strings.web')}/>
								</RadioGroup>
							}
							{newRecord && values.isVirtual &&
								<>
									<Divider/>
									<Text field='name' labelT='loyalty_card_section.fields.card_name' {...notEmpty()}/>
									<PhoneNumber
										field='validPhoneNumber'
										label={t('loyalty_card_section.fields.phone')}
										validate={v => /^\+[0-9]{11,14}$/g.test(v) ? null : t('validators.valid_phone_number')}
									/>
									<Text field='email' labelT='common.fields.email' disabled={values.isUser} />
									<Divider/>
								</>
							}
							{(!newRecord || !values.isVirtual) &&
								<ExpandableBlocks expanderRef={expandableBlocksRef} nested>
									<ContentBlock startExpanded label={t('common.strings.card')}>
										<Row>
											<Col xs className='narrow-col'>
												<Text value={newRecord ? '' : data.id} label={t('loyalty_card_section.fields.card_number')} disabled noForm shrink />
												<Checkbox field='isActive' label={t('common.fields.isActive')} />
											</Col>
											<Col xs className='narrow-col'>
												<Text field='accessCode' label={t('loyalty_card_section.fields.internet_access_code')} disabled shrink />
											</Col>
											<Col xs className='narrow-col'>
												<DateTimePicker value={data.lastUpdatedAt} noForm disabled label={t('loyalty_card_section.fields.last_used')} picker='date' shrink />
											</Col>
											<Col xs className='narrow-col'>
												<DateTimePicker value={data.lastUpdatedAt} noForm disabled label={t('common.fields.time')} picker='time' shrink />
											</Col>
											{ConfirmModal()}
										</Row>
									</ContentBlock>
									<ContentBlock label={t('loyalty_card_section.strings.balance')}>
										<Row>
											<Col xs className='narrow-col'>
												<NumberInput field='balance' label={t('loyalty_card_section.fields.balance')} toFixed={2} precision={2} disabled withControls={false} shrink />
											</Col>
											<Col xs className='narrow-col'>
												<NumberInput field='points' label={t('loyalty_card_section.fields.points')} disabled withControls={false} shrink />
												<Checkbox field='isPointsActive' label={t('common.fields.isActive')} />
											</Col>
											<Col xs className='narrow-col'>
												<NumberInput
													field='limit'
													labelT='loyalty_card_section.fields.card_limit'
													precision={2}
													withControls={false}
													shrink
												/>
											</Col>
											<Col xs className='narrow-col'>
												<NumberInput
													field='dayLimit'
													labelT='loyalty_card_section.fields.daily_limit'
													precision={2}
													withControls={false}
													shrink
												/>
											</Col>
										</Row>
									</ContentBlock>
									<ContentBlock blockId='owner' label={t('loyalty_card_section.strings.owner')}>
										<Row>
											<Col xs>
												<InformedText field='userId' hidden />
												{values.isUser
													? <Select
														disabled={multiEdit?.isActive}
														field='user'
														label={t('loyalty_card_section.fields.user')}
														remoteOptions={{
															query: listUsers
														}}
														withVirtualBody={true}
														valueField='id'
														labelField='name'
														isSearchable
														simpleValue={false}
														getOptionLabel={opt => opt.code ? `${opt.name} (${opt.code})` : opt.name}
														onChange={({ id, name }) => {
															formApi.setValue('userId', id)
															formApi.setValue('name', name)
														}}
														validate={(val, vals) => (vals && vals.isUser && !get(val, 'id') ? expandableBlocksRef?.current?.setExpandedId('owner') || t('validators.required') : null)}
														validateOnChange
													/>
													: <Text
														{...values.isUser && { hidden: true }}
														field='name'
														labelT='loyalty_card_section.fields.card_name'
													/>
												}
												<Text field='salutation' labelT='loyalty_card_section.fields.salutation' disabled={values.isUser} />
												<Text field='clientName' labelT='loyalty_card_section.fields.name' disabled={values.isUser} />
												<Text field='clientSurname' labelT='loyalty_card_section.fields.first_name' disabled={values.isUser} />
												<Text field='additive' labelT='loyalty_card_section.fields.additive' disabled={values.isUser} />
												<Checkbox
													field='isUser'
													label={t('loyalty_card_section.fields.employee_card')}
													disabled={multiEdit?.isActive}
													notify={['user']}
													validateOnChange
													onChange={e => {
														if(!e.target.checked) {
															formApi.setValue('userId', 0)
															formApi.setValue('user', {})
														}
													}} />
											</Col>
											<Col xs>
												<Text field='street' labelT='loyalty_card_section.fields.street' disabled={values.isUser} />
												<Text style={{ width: '80px' }} field='postcode' label={t('loyalty_card_section.fields.postcode_city')} placeholder={t('common.placeholders.text', { name: t('common.strings.post_code') })} shrink inline disabled={values.isUser} />
												<Text style={{ width: '200px' }}field='city' placeholder={t('common.placeholders.text', { name: t('common.strings.city') })} inline disabled={values.isUser} />
												<PhoneNumber
													field='validPhoneNumber'
													label={t('loyalty_card_section.fields.phone')}
													validate={(v) => {
														// CW-1569: Telephone number should not be a mandatory field for RFID loyalty card
														// We don't validate online cards or employee cards or empty phone number
														if (values.isVirtual || values.isUser || !v || /^\+[0-9]{11,14}$/g.test(v)) {
															return null
														}
														if(expandableBlocksRef?.current?.setExpandedId) {
															expandableBlocksRef.current.setExpandedId('owner')
														}
														return t('validators.valid_phone_number')
													}}
													disabled={values.isUser}
												/>
												<Text field='email' labelT='common.fields.email' disabled={values.isUser} />
												<StyledIconContainer>
													{(values.validPhoneNumber || values.email) &&
														<SendMessageButton itemId={data.id} link={data.virtualCardLink} company={appData.name}/>
													}
													{data?.statusVirtual?.type?.includes('APPLE') &&
														<AppleIcon/>
													}
													{data?.statusVirtual?.type?.includes('GOOGLE') &&
														<GoogleIcon/>
													}
												</StyledIconContainer>
											</Col>
										</Row>
									</ContentBlock>
									<ContentBlock label={t('loyalty_card_section.strings.type')}>
										<Row>
											<Col xs>
												<Select
													field='type'
													labelT='loyalty_card_section.fields.card_type'
													remoteOptions={{
														query: loadAppConstants,
														variables: { type: 74, language: i18n.language },
														modifyOptions: vals => vals.map(({ value, ...rest }) => ({ ...rest, value: Number(value) }))
													}}
													onChange={() => formApi.setValues({ ...values, creditClientId: null, creditClient: null })}
													disabled={!!values.lastUse || multiEdit?.isActive}
												/>
												<InformedText field='creditClientId' hidden />
												<Select
													key={1}
													field='creditClient'
													labelT='loyalty_card_section.fields.debit_card'
													remoteOptions={{
														query: listCreditClients,
														variables: { params: { sort: [{ field: 'clientNumber', order: 'ASC' }] } }
													}}
													withVirtualBody
													isSearchable
													valueField='id'
													labelField='name'
													onChange={({ id }) => formApi.setValue('creditClientId', id)}
													simpleValue={false}
													disabled={!(Number(values.type) === 3)}
												/>
												<NumberInput
													field='discount.freePercent'
													label={`${t('loyalty_card_section.fields.discount')}, %`}
													format='percent'
													onChange={() => {
														const connectedField = 'discount.masterPercent'
														if(get(values, connectedField)) {
															formApi.setValue(connectedField, 0)
														}
														formApi.setValue('discount.numberOfPercent', 0)
													}}
													precision={2}
													withControls={false}
													inline
													shrink />
												<NumberInput
													field='discount.masterPercent'
													label={`${t('loyalty_card_section.fields.discount_management')}, %`}
													format='percent'
													onChange={() => {
														const connectedField = 'discount.freePercent'
														if(get(values, connectedField)) {
															formApi.setValue(connectedField, 0)
														}
														formApi.setValue('discount.numberOfPercent', 0)
													}}
													precision={2}
													withControls={false}
													inline
													shrink />
												<Select
													field='discount.numberOfPercent'
													label={t('loyalty_card_section.fields.discount_number')}
													remoteOptions={{
														query: getDiscountGroupNames,
														variables: { language: langCode },
														modifyOptions: vals => vals.map((item, i) => ({ value: i + 1, label: item })),
														additionalOptions: [{ value: 0, label: '<kein>' }]
													}}
													onChange={({ value }) => {
														if(value) {
															const { freePercent, masterPercent } = get(values, 'discount', {})
															if(freePercent) {
																formApi.setValue('discount.freePercent', 0)
															}
															if(masterPercent) {
																formApi.setValue('discount.masterPercent', 0)
															}
														}
													}}
													shrink />
											</Col>
											<Col xs={6}>
												<Checkbox field='balanceDiscount' label={t('loyalty_card_section.fields.loaded_discount')} />
												<Checkbox field='alwaysDiscount' label={t('loyalty_card_section.fields.discount_net_product')} />
												<Checkbox field='cashLoad' label={t('loyalty_card_section.fields.load_cash')} />
												<Checkbox field='balancePoints' label={t('loyalty_card_section.fields.points_on_load')} />
												{/*<Button text='RFID Karte zuweisen' />*/}
												<Divider height={15} />
												<div>
													<Text field='rfid' label={t('loyalty_card_section.fields.rfid')} disabled inline/>
													<Text field='barCode' disabled={multiEdit?.isActive} label={t('loyalty_card_section.fields.ean_code')} inline/>
												</div>
											</Col>
										</Row>
									</ContentBlock>
								</ExpandableBlocks>
							}
							{ConfirmButton(formState)}
							{newRecord &&
								<>
									<Divider />
									<Button type='submit' text={t('common.buttons.create')} />
								</>
							}
						</>
					)}
				</Form>
			</ContentBlock>

			{!newRecord && <Transactions itemId={data.id} t={t} data={data} />}
			{/*this.state.showImportModal &&
				<Modal width={500} label='Kundenkarten-Import' unmount={() => this.setState({ showImportModal: false })}>
					<CardImport/>
				</Modal>
			*/}
		</ExpandableBlocks>
	)
}


export default Loyaltycard
